import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Loader2, CheckCircle, Music } from 'lucide-react';
import { API } from '../config/api';
import { Helmet } from 'react-helmet-async';

export default function ApplyPage() {
  const [form, setForm] = useState({
    name: '', email: '', bio: '', slug_suggestion: '', why_apply: '',
    instagram: '', spotify: '', soundcloud: '', website: '', sample_track_url: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required'); return;
    }
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/featured-artists/apply`, form);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed — try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="apply-page">
      <Helmet><title>Apply · Featured Artist · MeloDrop</title></Helmet>
      {/* Hero */}
      <div className="relative h-[48vh] min-h-[320px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#0a0a0a] to-[#000]" />
        <div className="absolute inset-0 mix-blend-overlay opacity-25"
          style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'2\' stitchTiles=\'stitch\'/></filter><rect width=\'120\' height=\'120\' filter=\'url(%23n)\' opacity=\'0.45\'/></svg>")' }} />
        <Link to="/" className="absolute top-5 left-5 z-20 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white backdrop-blur-md bg-black/30 px-3 py-2 border border-white/10">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <div className="absolute inset-x-0 bottom-0 px-4 sm:px-12 pb-10 max-w-4xl mx-auto">
          <p className="text-[10px] tracking-[0.4em] text-[#FF3B30] mb-3 animate-fade-up" style={{ animationDelay: '0.1s' }}>FEATURED ARTIST APPLICATION</p>
          <h1 className="font-cabinet text-5xl sm:text-7xl font-black uppercase tracking-tighter leading-[0.85] animate-fade-up" style={{ animationDelay: '0.2s' }}>
            JOIN THE<br /><span className="text-[#DAA520]">ROSTER</span>
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 sm:px-12 py-12">
        {submitted ? (
          <div className="bg-[#0F1A0F] border-2 border-[#22C55E] p-8 text-center" data-testid="apply-success">
            <div className="w-16 h-16 mx-auto bg-[#22C55E] text-black rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-9 h-9" />
            </div>
            <h2 className="font-cabinet text-3xl font-black uppercase mb-2">APPLICATION RECEIVED</h2>
            <p className="text-[#A3A3A3] mb-6">We&apos;ll review your application and email you when there&apos;s a decision.</p>
            <Link to="/" className="inline-block px-6 py-3 bg-[#DAA520] hover:bg-white text-black font-black uppercase tracking-wider transition-colors" data-testid="apply-back-home">
              BACK TO HOMEPAGE
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
              Tell us about yourself. We approve a small number of artists each month to feature on the platform. You&apos;ll get your own sub-page with playable music, gallery, videos, and the ability to sell directly.
            </p>

            <Field label="Artist Name *">
              <input value={form.name} onChange={update('name')} required maxLength={120}
                className="input-dark" placeholder="Your stage name" data-testid="apply-name" />
            </Field>
            <Field label="Email *">
              <input type="email" value={form.email} onChange={update('email')} required maxLength={200}
                className="input-dark" placeholder="you@example.com" data-testid="apply-email" />
            </Field>
            <Field label="Preferred URL slug (optional)">
              <input value={form.slug_suggestion} onChange={update('slug_suggestion')} maxLength={60}
                className="input-dark font-mono" placeholder="e.g. my-stage-name → /artist/my-stage-name" data-testid="apply-slug" />
            </Field>
            <Field label="Short Bio">
              <textarea value={form.bio} onChange={update('bio')} rows={4} maxLength={2000}
                className="input-dark resize-none" placeholder="A few sentences about your sound, where you're based, what you've released." data-testid="apply-bio" />
            </Field>
            <Field label="Why MeloDrop?">
              <textarea value={form.why_apply} onChange={update('why_apply')} rows={3} maxLength={1000}
                className="input-dark resize-none" placeholder="What are you hoping to do with this platform?" data-testid="apply-why" />
            </Field>

            <div className="border-t border-[#262626] pt-5">
              <p className="text-[10px] tracking-[0.3em] text-[#DAA520] uppercase font-bold mb-3">YOUR LINKS</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field compact label="Instagram"><input value={form.instagram} onChange={update('instagram')} className="input-dark" placeholder="https://instagram.com/…" data-testid="apply-instagram" /></Field>
                <Field compact label="Spotify"><input value={form.spotify} onChange={update('spotify')} className="input-dark" placeholder="https://open.spotify.com/artist/…" data-testid="apply-spotify" /></Field>
                <Field compact label="SoundCloud"><input value={form.soundcloud} onChange={update('soundcloud')} className="input-dark" placeholder="https://soundcloud.com/…" data-testid="apply-soundcloud" /></Field>
                <Field compact label="Website"><input value={form.website} onChange={update('website')} className="input-dark" placeholder="https://…" data-testid="apply-website" /></Field>
              </div>
              <div className="mt-3">
                <Field compact label="Sample Track URL (optional)">
                  <input value={form.sample_track_url} onChange={update('sample_track_url')} className="input-dark" placeholder="Public link to a track you'd like us to listen to" data-testid="apply-sample" />
                </Field>
              </div>
            </div>

            {error && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-sm px-4 py-3" data-testid="apply-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting}
              className="w-full h-14 bg-[#DAA520] hover:bg-white text-black font-black uppercase tracking-wider text-base flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              data-testid="apply-submit">
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> SUBMITTING…</> : <><Send className="w-5 h-5" /> SUBMIT APPLICATION</>}
            </button>
            <p className="text-[10px] text-[#525252] text-center"><Music className="w-3 h-3 inline mr-1 -mt-0.5" /> Review typically takes 3–7 days</p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, compact = false }) {
  return (
    <div>
      <label className={`block text-[10px] uppercase tracking-[0.3em] font-bold ${compact ? 'text-[#A3A3A3]' : 'text-[#DAA520]'} mb-1.5`}>{label}</label>
      {children}
    </div>
  );
}
